import jschardet from 'jschardet';
import Papa from 'papaparse';
import { type ReactNode, useRef } from 'react';
import styled, { css } from 'styled-components';

import { CenterFlex, VFlex } from '@/components/base';
import { Button } from '@/components/shadcn/ui/button';
import Typo from '@/components/Typography/Typo';
import { radius } from '@/styles/sizes';
import { convertToUTF8 } from '@/utils/util';

type Props = {
  icon: ReactNode;
  title: string;
  desc: string;
  onClick: () => void;
  disabled?: boolean;
  // CSV 업로드 관련 props
  onCsvUpload?: (data: Record<string, string>[]) => void;
  csvTemplateHref?: string;
  csvTemplateFileName?: string;
};

const TopicTypeCard = (props: Props) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !props.onCsvUpload) return;

    const reader = new FileReader();
    reader.onload = function (event) {
      const content = event.target?.result as string;
      const csvResult = content.split(/\r|\n|\r\n/);
      const isWindows1252 =
        jschardet.detect(csvResult.toString()).encoding === 'windows-1252';

      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        transform(value) {
          return isWindows1252 ? convertToUTF8(value) : value;
        },
        complete: (results) => {
          props.onCsvUpload?.(results.data as Record<string, string>[]);
        },
      });
    };
    reader.readAsBinaryString(file);

    // input 초기화 (같은 파일 재선택 가능하도록)
    e.target.value = '';
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Style g={40} a="center" disabled={props.disabled}>
      <CenterFlex className="icon">{props.icon}</CenterFlex>
      <VFlex g={8}>
        <Typo type="label0" weight="sb">
          {props.title}
        </Typo>
        <Typo type="body2">{props.desc}</Typo>
      </VFlex>
      <div className="flex flex-col gap-2">
        <Button
          size="sm"
          onClick={props.onClick}
          disabled={props.disabled}
          className="w-full cursor-pointer"
        >
          Select
        </Button>
        {props.onCsvUpload && (
          <VFlex g={1} a="center">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            <Button
              size="sm"
              variant="outline"
              onClick={handleUploadClick}
              disabled={props.disabled}
              className="w-full cursor-pointer"
            >
              Upload CSV
            </Button>
            {props.csvTemplateHref && (
              <a
                href={props.csvTemplateHref}
                download={props.csvTemplateFileName}
                target="_self"
                className="template-link"
              >
                <Typo type="label3" className="text-gray-600 underline">
                  Download .csv template
                </Typo>
              </a>
            )}
          </VFlex>
        )}
      </div>
    </Style>
  );
};

type StyleProps = {
  disalbed?: boolean;
};

const Style = styled(VFlex)<StyleProps>`
  width: 300px;
  padding: 40px 20px;
  background-color: ${(props) => props.theme.colors.gray200};
  border-radius: ${radius.token['3xl']}px;
  ${({ disabled }) =>
    disabled &&
    css`
      background-color: ${(props) => props.theme.colors.gray300};
      opacity: 0.6;
    `}
  .icon {
    width: 40px;
    aspect-ratio: 1;
    font-size: 22px;
    background-color: ${(props) => props.theme.colors.gray400};
    border-radius: 20px;
  }
`;

export default TopicTypeCard;
